import { useCallback, useEffect, useState, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Button, Text } from "@/components/atoms"
import { ButtonGroup, Input, Select, SelectOption } from "@/components/molecules"
import { useModal } from "@/components/molecules/Modal/ModalProvider"
import { CompanySelectionModalContent } from "@/components/MentoringsForm/CompanySelectionModalContent"
import { MentoringsDash } from "@/components/MentoringsList/MentoringsDash"
import { MentoringsList } from "@/components/MentoringsList/MentoringsList"
import { useAuth } from "@/contexts/AuthContext"
import { Toolbar } from "@/organisms/Toolbar"
import { getAllMentoringsV2 } from "@/services/mentoringServices"
import { getMentoresLista } from "@/services/reportService"
import type { MentoringDataType } from "@/types/mentoring"
import { SearchableSelect } from "@/components/molecules/SearchableSelect"
import { ControlBar } from "@/components/organisms/ControlBar"
import { useSticky } from "@/hooks/useSticky"
import { dateMask } from "@/utils/masks"
import RescheduleMentoringModal from "@/components/MentoringBooking/RescheduleMentoringModal"
import CancelScheduleMentoringModal from "@/components/MentoringBooking/CancelScheduleMentoringModal"

const Mentorings = () => {
  const { user } = useAuth()
  const { showModal, hideModal } = useModal()
  const navigate = useNavigate()

  // Refs para Scroll Infinito
  const observerLoader = useRef<HTMLDivElement>(null)

  // --- States de Filtro e UI ---
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [startDate, setStartDate] = useState<string>("")
  const [endDate, setEndDate] = useState<string>("")
  const [searchTerm, setSearchTerm] = useState("")
  const [mentorFilter, setMentorFilter] = useState<string>("")
  const [tipoMentorFilter, setTipoMentorFilter] = useState<string>("")
  const [mentors, setMentors] = useState<{ value: string; label: string }[]>([])

  // States para Debounce (evitar excesso de requests)
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [debouncedDates, setDebouncedDates] = useState({ start: "", end: "" })

  const [sortOrder, setSortOrder] = useState("")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")
  const [isFilterOpened, setIsFilterOpened] = useState(true)
  const [selectedControlTab, setSelectedControlTab] = useState<string>("filters")
  const [sentinelRef] = useSticky<HTMLDivElement>({ rootMargin: "-60px" })

  // --- States de Dados ---
  const [mentoringsData, setMentoringsData] = useState<MentoringDataType[]>([])
  const [metaData, setMetaData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [itemsPerPage, setItemsPerPage] = useState("10")
  const [dashData, setDashData] = useState<any>(null)

  const breadcrumbItems = [
    { href: "/", label: "Início" },
    { href: "/mentorings", label: "Mentorias", isCurrent: true },
  ]

  // --- Carregar mentores para o filtro (somente admin) ---
  useEffect(() => {
    if (!user?.admin) return
    getMentoresLista()
      .then((data) =>
        setMentors([
          { value: "", label: "Todos os mentores" },
          ...data.map((m) => ({ value: String(m.mentorId), label: m.nome })),
        ])
      )
      .catch(() => setMentors([]))
  }, [user?.admin])

  // --- Lógica de Debounce ---
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setDebouncedDates({ start: startDate, end: endDate })
    }, 500)
    return () => clearTimeout(handler)
  }, [searchTerm, startDate, endDate])

  // --- Data Fetching ---
  const fetchMentorings = useCallback(
    async (pageToFetch: number = 1, isInitial: boolean = false) => {
      try {
        if (isInitial) setLoading(true)
        else setIsFetchingMore(true)

        const formatDateParam = (dateStr: string | null) => {
          if (!dateStr || dateStr.length !== 10) return null
          const [day, month, year] = dateStr.split("/")
          return `${year}-${month}-${day}`
        }
        const dataInicio = formatDateParam(debouncedDates.start)
        const dataFim = formatDateParam(debouncedDates.end)

        // Enviando parâmetros de filtro para a API
        const response = await getAllMentoringsV2({
          page: pageToFetch,
          limit: itemsPerPage,
          search: debouncedSearch,
          status: statusFilter.join(","),
          dataInicio: dataInicio,
          dataFim: dataFim,
          ...(mentorFilter ? { mentorIdFiltro: mentorFilter } : {}),
          ...(tipoMentorFilter ? { tipoMentor: tipoMentorFilter } : {}),
        } as any)

        const newData = response.data || []
        const newMeta = response.meta

        setMentoringsData((prev) => (pageToFetch === 1 ? newData : [...prev, ...newData]))
        setMetaData(newMeta)

        if (pageToFetch === 1) {
          setDashData(newMeta)
        }
      } catch (error) {
        console.error("Error loading mentorings:", error)
      } finally {
        setLoading(false)
        setIsFetchingMore(false)
      }
    },
    [debouncedSearch, statusFilter, debouncedDates, itemsPerPage, mentorFilter, tipoMentorFilter]
  )

  // Reset e Carga Inicial ao mudar filtros ou busca
  useEffect(() => {
    fetchMentorings(1, true)
  }, [
    debouncedSearch,
    statusFilter,
    debouncedDates,
    mentorFilter,
    tipoMentorFilter,
    fetchMentorings,
  ])

  // Infinite Scroll Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && metaData?.hasMore && !loading && !isFetchingMore) {
          fetchMentorings(metaData.page + 1)
        }
      },
      { threshold: 0.1 }
    )

    if (observerLoader.current) observer.observe(observerLoader.current)
    return () => observer.disconnect()
  }, [metaData, loading, isFetchingMore, fetchMentorings])

  // --- Sort Logic (Aplicada localmente sobre os dados carregados) ---
  const sortedMentorings = useMemo(() => {
    let processed = [...mentoringsData]

    if (sortOrder) {
      processed.sort((a, b) => {
        if (sortOrder === "dataHoraMentoria") {
          const dateA = new Date(a.dataHoraMentoria).getTime()
          const dateB = new Date(b.dataHoraMentoria).getTime()
          return sortDirection === "asc" ? dateA - dateB : dateB - dateA
        }

        let valA = a[sortOrder as keyof MentoringDataType] ?? ""
        let valB = b[sortOrder as keyof MentoringDataType] ?? ""

        if (valA < valB) return sortDirection === "asc" ? -1 : 1
        if (valA > valB) return sortDirection === "asc" ? 1 : -1
        return 0
      })
    }
    return processed
  }, [mentoringsData, sortOrder, sortDirection])

  // --- Handlers ---
  const handleOpenCompanySelection = () => {
    showModal({
      title: "Selecione a empresa",
      size: "xl",
      content: (
        <CompanySelectionModalContent
          onSelectCompany={(company) => {
            hideModal()
            navigate(`/mentorings/new/${company.empId}`)
          }}
        />
      ),
    })
  }

  const handleFormSuccess = () => {
    hideModal()
    fetchMentorings(1, true)
  }

  const handleOpenRescheduleModal = (mentoring: MentoringDataType) => {
    showModal({
      title: "Reagendar Mentoria",
      content: (
        <RescheduleMentoringModal
          mentoring={mentoring}
          onSuccess={handleFormSuccess}
        />
      ),
      size: "xl",
    })
  }

  const handleOpenCancelModal = (mentoring: MentoringDataType) => {
    showModal({
      title: "Cancelar Mentoria",
      content: (
        <CancelScheduleMentoringModal
          mentoring={mentoring}
          onSuccess={handleFormSuccess}
        />
      ),
      size: "xl",
    })
  }

  const handleTabChange = (tabId: string) => {
    setSelectedControlTab(tabId)
    setIsFilterOpened(tabId === selectedControlTab ? !isFilterOpened : true)
  }

  const appliedFiltersCount =
    statusFilter.length +
    (startDate ? 1 : 0) +
    (endDate ? 1 : 0) +
    (mentorFilter ? 1 : 0) +
    (tipoMentorFilter ? 1 : 0)

  const itemsPerPageOptions: SelectOption[] = [
    { label: "10", value: "10" },
    { label: "50", value: "50" },
    { label: "100", value: "100" },
    { label: "200", value: "200" },
  ]

  return (
    <section className="flex flex-col gap-4 duration-700 animate-in fade-in">
      <Toolbar breadcrumbItems={breadcrumbItems} />

      <section className="px-8 pt-20">
        <MentoringsDash
          data={dashData}
          onMentoringClick={(m) => navigate(`/mentorings/${m.mentoriaId}/${m.empId}`)}
        />
      </section>

      <div ref={sentinelRef} />

      <div className="sticky top-14 z-20 flex flex-col justify-between gap-4 bg-dark px-8 pb-2 pt-4">
        <ControlBar
          isControlBarOpened={isFilterOpened}
          currentTab={selectedControlTab}
          height={user?.admin ? 280 : 180}
          onTabChange={handleTabChange}
          showClearFilters={appliedFiltersCount > 0}
          onClearFilters={() => {
            setStatusFilter([])
            setStartDate("")
            setEndDate("")
            setMentorFilter("")
            setTipoMentorFilter("")
          }}
          tabs={[
            { label: "Filtros", id: "filters", counter: appliedFiltersCount, iconName: "filters" },
          ]}
        >
          <section className="flex flex-col gap-4">
            <div>
              <Text
                className="ml-4"
                variant="label"
              >
                Status
              </Text>
              <ButtonGroup
                items={[
                  { value: "1", label: "Pendente" },
                  { value: "2", label: "Realizado" },
                  { value: "3", label: "Finalizado" },
                  { value: "4", label: "No-show" },
                  { value: "5", label: "Cancelado" },
                  { value: "6", label: "Avaliado" },
                ]}
                value={statusFilter}
                multiple
                onSelectionChange={(v) => setStatusFilter(v as string[])}
              />
            </div>
            <div className="flex gap-2">
              <div className="max-w-[300px]">
                <Input
                  id="filter-date-start"
                  label="De"
                  placeholder="DD/MM/AAAA"
                  value={startDate}
                  onChange={(e) => setStartDate(dateMask(e.target.value))}
                />
              </div>
              <div className="max-w-[300px]">
                <Input
                  id="filter-date-end"
                  label="Até"
                  placeholder="DD/MM/AAAA"
                  value={endDate}
                  onChange={(e) => setEndDate(dateMask(e.target.value))}
                />
              </div>
            </div>
            {user?.admin && (
              <div className="flex flex-wrap items-end justify-between gap-4 border-t border-white/10 pt-4">
                <div className="w-72">
                  <SearchableSelect
                    id="mentor-filter"
                    label="Mentor"
                    placeholder="Todos os mentores"
                    value={mentorFilter}
                    onValueChange={setMentorFilter}
                    options={mentors}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Text variant="label">Tipo de sessão</Text>
                  <ButtonGroup
                    items={[
                      { value: "", label: "Todos" },
                      { value: "principal", label: "Principal" },
                      { value: "especialista", label: "Especialista" },
                    ]}
                    value={tipoMentorFilter}
                    onSelectionChange={(v) => setTipoMentorFilter(v as string)}
                  />
                </div>
              </div>
            )}
          </section>
        </ControlBar>

        <div className="flex flex-wrap justify-between">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Input
                id="search-mentorings"
                iconLeft="search"
                placeholder="Pesquisar"
                variant="outline"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Select
                id="sort-order"
                variant="filled"
                options={[
                  { value: "statusMentoria", label: "Status" },
                  { value: "dataHoraMentoria", label: "Data" },
                  { value: "mentoradoNota", label: "Avaliação" },
                ]}
                value={sortOrder}
                onValueChange={setSortOrder}
                placeholder="Ordenar por"
              />
              {sortOrder && (
                <Button
                  size="sm"
                  variant="outline"
                  icon={sortDirection === "asc" ? "sort-circle-up" : "sort-circle-down"}
                  iconOnly
                  onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
                />
              )}
              <div className="flex items-center gap-2">
                <Select
                  id="itemsPerPage"
                  variant="filled"
                  onValueChange={(e) => setItemsPerPage(e)}
                  placeholder="10 Itens por página"
                  value={itemsPerPage}
                  options={itemsPerPageOptions}
                />
                <Text className="text-nowrap">Itens por página</Text>
              </div>
            </div>
            <Text
              className="text-dark-300"
              variant="body"
            >
              {loading
                ? "Carregando..."
                : `Exibindo ${mentoringsData.length} de ${metaData?.total || 0} mentorias`}
            </Text>
          </div>
          <Button
            icon="plus-hexagon"
            onClick={handleOpenCompanySelection}
          >
            In Company
          </Button>
        </div>
      </div>

      <section className="grid grid-cols-[repeat(auto-fit,minmax(400px,1fr))] gap-4 px-8 py-4">
        {sortedMentorings.map((mentoring) => (
          <MentoringsList
            key={mentoring.mentoriaId}
            onCardClick={() => navigate(`/mentorings/${mentoring.mentoriaId}/${mentoring.empId}`)}
            mentoring={mentoring}
            onRescheduleClick={() => handleOpenRescheduleModal(mentoring)}
            onCancelScheduleClick={() => handleOpenCancelModal(mentoring)}
          />
        ))}

        {/* Sentinel Loader para o Infinite Scroll */}
        <div
          ref={observerLoader}
          className="col-span-full flex flex-col items-center justify-center py-10"
        >
          {isFetchingMore && (
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <Text
                variant="body"
                className="font-medium text-primary"
              >
                Carregando mais mentorias...
              </Text>
            </div>
          )}

          {!metaData?.hasMore && mentoringsData.length > 0 && (
            <div className="flex w-full items-center gap-4 px-4">
              <div className="h-[1px] flex-1 bg-dark-400 opacity-20" />
              <Text
                variant="body"
                className="text-dark-400"
              >
                Fim da listagem
              </Text>
              <div className="h-[1px] flex-1 bg-dark-400 opacity-20" />
            </div>
          )}

          {!loading && mentoringsData.length === 0 && (
            <Text className="text-dark-300">Nenhuma mentoria encontrada.</Text>
          )}
        </div>
      </section>
    </section>
  )
}

export default Mentorings
